import { useEffect, useRef, useState } from 'react';
import { supabase } from '@/shared/lib/supabase';
import { useAuth } from '@/shared/contexts/AuthContext';

/**
 * useStaffRole — resolves the current user's Phase 11 sales role + company.
 *
 * Phase 11 external sales staff DO NOT carry `auth.users.raw_app_meta_data.role`
 * (per SP-18). They're recognised by their `public.staff_profile` row.
 *
 * Returns:
 *   - `loading: true` until resolved
 *   - `staffRole: null` if user has no active staff_profile row
 *   - `staffRole: 'admin' | 'sales_manager' | 'sales_agent' | 'support' | 'qa'`
 *   - `companyId: string | null` — staff_profile.company_id
 *
 * LD-S9 (2026-06-05): `sales_company_admin` deprecated — `sales_manager` IS
 * the company-side admin. See migration 20260605120000.
 */

export type StaffRole =
  | 'admin'
  | 'sales_manager'
  | 'sales_agent'
  | 'support'
  | 'qa';

export interface StaffRoleState {
  loading: boolean;
  staffRole: StaffRole | null;
  companyId: string | null;
  isSalesStaff: boolean;
  isManagerOrAdmin: boolean;
  isAdmin: boolean;
}

const SALES_ROLES: StaffRole[] = ['sales_agent', 'sales_manager'];

export function useStaffRole(): StaffRoleState {
  const { user, loading: authLoading } = useAuth();
  const [state, setState] = useState<Omit<StaffRoleState, 'loading'>>({
    staffRole: null,
    companyId: null,
    isSalesStaff: false,
    isManagerOrAdmin: false,
    isAdmin: false,
  });
  const [loading, setLoading] = useState(true);
  // Tracks the last user id we resolved a role for. A token refresh on tab
  // refocus can hand us a new `user` reference with the SAME id; when that
  // happens we refetch in the background WITHOUT flipping `loading` back to
  // true — otherwise the route guards (StaffRoute / SalesRoute / HomeRedirect)
  // would flash their spinner and unmount the whole page subtree, losing any
  // in-progress form state. We only gate on `loading` for a genuinely new
  // user (first sign-in or an account switch), never for a same-user refetch.
  const prevUserId = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (authLoading) return;
    const uid = user?.id ?? null;
    const isSameUser = uid !== null && uid === prevUserId.current;
    prevUserId.current = uid;
    if (!user) {
      setState({ staffRole: null, companyId: null, isSalesStaff: false, isManagerOrAdmin: false, isAdmin: false });
      setLoading(false);
      return;
    }
    if (!isSameUser) setLoading(true);
    (async () => {
      try {
        const { data } = await supabase
          .from('staff_profile')
          .select('role, company_id, active')
          .eq('id', user.id)
          .maybeSingle();
        if (cancelled) return;
        const active = !!data?.active;
        const role = (active ? (data!.role as StaffRole) : null);
        const companyId = active ? (data!.company_id as string) : null;
        const isSalesStaff = role !== null && SALES_ROLES.includes(role);
        const isAdmin = role === 'admin';
        const isManagerOrAdmin = isAdmin || role === 'sales_manager';
        setState({ staffRole: role, companyId, isSalesStaff, isManagerOrAdmin, isAdmin });
        setLoading(false);
      } catch {
        if (cancelled) return;
        setState({ staffRole: null, companyId: null, isSalesStaff: false, isManagerOrAdmin: false, isAdmin: false });
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user, authLoading]);

  return { loading, ...state };
}
